import React, {useCallback, useEffect, useRef} from 'react';
import {MenuDropdown} from "../../utils/menu/MenuDropDown";
import {InputSearch} from "../../utils/inputSearch/InputSearch";
import ChatListPeriod from "./utils/ChatListPeriod";
import ChatListSort from "./utils/ChatListSort";
import ChatListTabs from "./utils/ChatListTabs";
import {ChatListItem, ChatReminderItem} from "./utils/ChatListItem";
import {useDispatch, useSelector} from "react-redux";
import {chatListAction, chatTotalsAction} from "../../actions/chatAction";
import {UPDATE_CHAT_FILTER} from "../../constants";
import ChatListSearchAll from "./utils/ChatListSearchAll";
import {AutoSizer, List, CellMeasurer, CellMeasurerCache} from 'react-virtualized';

const ChatList = (props) => {

  const dispatch = useDispatch();
  const list = useSelector(state => state.chat.list);
  const reminders = useSelector(state => state.chat.reminders);
  /*const total = useSelector(state => state.chat.total);
  const loading = useSelector(state => state.chat.loading);*/
  const filter = useSelector(state => state.chat.filter);
  const totals = useSelector(state => state.chat.totals);
  const totalsReload = useSelector(state => state.chat.totalsReload);
  const abortListRef = useRef(null);
  const abortTotalsRef = useRef(null);
  const listRef = useRef(null);
  const measurerCacheRef = useRef(new CellMeasurerCache({
    defaultHeight: 80,
    fixedWidth: true
  }));

  const handleChangeFilter = useCallback((field, value, data) => {
    dispatch({type: UPDATE_CHAT_FILTER, data: typeof data === 'undefined' ? {[field]: value} : {...data}});
  }, [dispatch]);

  // Abort the previous request in the cleanup rather than at the start of the next run:
  // this also fires when leaving the page (the request used to be left hanging), and React
  // guarantees the "cancel the old one, then start the new one" ordering for us.
  useEffect(() => {
    const abortNew = new AbortController();
    abortListRef.current = abortNew;
    dispatch(chatListAction(filter, abortNew.signal)).then(() => {
      if (abortListRef.current === abortNew) {
        abortListRef.current = null;
      }
    });
    return () => {
      abortNew.abort();
    };
  }, [dispatch, filter]);

  useEffect(() => {
    // Invalidate measurements when the list changes
    if (measurerCacheRef.current) {
      measurerCacheRef.current.clearAll();
    }
    if (listRef.current) {
      listRef.current.recomputeRowHeights();
      listRef.current.forceUpdateGrid();
    }
  }, [list]);

  useEffect(() => {
    if (!totalsReload) {
      return;
    }
    const abortNew = new AbortController();
    abortTotalsRef.current = abortNew;
    dispatch(chatTotalsAction(filter, abortNew.signal)).then(() => {
      if (abortTotalsRef.current === abortNew) {
        abortTotalsRef.current = null;
      }
    });
    return () => {
      abortNew.abort();
    };
  }, [dispatch, filter, totalsReload]);

  const rowRenderer = ({index, key, parent, style}) => {
    const item = list[index];
    return (
      <CellMeasurer
        cache={measurerCacheRef.current}
        columnIndex={0}
        key={key}
        parent={parent}
        rowIndex={index}
      >
        {({registerChild}) => (
          <div ref={registerChild} style={style}>
            <ChatListItem item={item}/>
          </div>
        )}
      </CellMeasurer>
    );
  };

  return <aside className="sidebar-chat">
    <div className="sidebar-chat__top">
      <MenuDropdown filter={filter} handleChange={handleChangeFilter}/>
      <InputSearch value={filter.search} handleChange={handleChangeFilter} className="sidebar-chat__input"/>
      <ChatListSearchAll search_all={filter.search_all} handleChange={handleChangeFilter}/>
      <ChatListPeriod filter={filter} totals={totals} handleChange={handleChangeFilter}/>
      <ChatListSort sort={filter.sort} handleChange={handleChangeFilter}/>
    </div>

    <div className="tabs">
      <ChatListTabs tab={filter.tab} totals={totals} handleChange={handleChangeFilter}/>

      <div className="tabs__content tabs__content--active">
        <div className="messages">
          {reminders.map((i) => (
            <ChatReminderItem key={i.id} item={i}/>
          ))}
          <div style={{height: '100%'}}>
          <AutoSizer>
            {({width, height}) => (
              <List
                ref={listRef}
                width={width}
                height={height}
                rowCount={list.length}
                deferredMeasurementCache={measurerCacheRef.current}
                rowHeight={measurerCacheRef.current.rowHeight}
                rowRenderer={rowRenderer}
                overscanRowCount={12}
              />
            )}
          </AutoSizer>
          </div>
        </div>
      </div>
    </div>

  </aside>;
}

export default ChatList
